<script setup lang="ts">
// Game page — score hero (teams, status, kickoff, league/round), the game's
// betting line + slip, then the GameDetail panels (line scores / box score /
// play-by-play). Polls while live.
const api = useApi()
const route = useRoute()
const eventId = computed(() => String(route.params.id || ''))
const { fmtDateTime } = useClientFormat()
const { user } = useAuth()

definePageMeta({ middleware: 'auth-optional' })

// the "scores" crumb returns to the scoreboard the user left: AppNav's smart
// back prefers history (restoring league + tab), and the href falls back to the
// scoreboard filtered to this game's league for deep-linked visits
const crumbs = computed(() => [
  { label: 'scores', to: data.value?.league ? `/scoreboard?sport=${encodeURIComponent(data.value.league)}` : '/scoreboard' },
  { label: 'game' },
])

interface GameView {
  event_id: string; league: string; league_title: string; group: string
  home_team: string; away_team: string; commence_time: string | null
  home_logo: string; away_logo: string
  status: string; final: boolean; home_score: number | null; away_score: number | null
  detail: any
}

const { data, pending, refresh } = await useAsyncData<GameView | null>(
  () => `game-${eventId.value}`,
  () => eventId.value
    ? api.get<GameView>(`/scoreboard/game/${encodeURIComponent(eventId.value)}`).catch(() => null)
    : Promise.resolve(null),
  { watch: [eventId], server: false })

const statusLabel = computed(() => {
  const g = data.value
  if (!g) return ''
  if (g.final) return 'Final'
  if (g.status === 'live') return 'Live'
  if (g.status === 'postponed') return 'Postponed'
  if (g.status === 'cancelled') return 'Cancelled'
  return fmtDateTime(g.commence_time)
})

// You can only bet a game that hasn't started: hide the odds + slip once it's
// live/final (or postponed/cancelled), or once kickoff has passed. The status
// flip from the poll below keeps this correct as a game goes live while viewed.
const bettable = computed(() => {
  const g = data.value
  if (!g) return false
  if (g.final || g.status === 'live' || g.status === 'postponed' || g.status === 'cancelled') return false
  if (g.commence_time && new Date(g.commence_time).getTime() <= Date.now()) return false
  return true
})

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  timer = setInterval(() => {
    if (document.visibilityState === 'visible' && data.value && !data.value.final) refresh()
  }, 20_000)
})
onBeforeUnmount(() => { if (timer) clearInterval(timer) })
</script>

<template>
  <div>
    <AppNav :crumbs="crumbs" />

    <div class="wrap">
      <div v-if="data" class="game">
        <p class="lg mono">{{ data.league_title || data.league }}<span v-if="data.group"> · {{ data.group }}</span></p>
        <div class="hero" :class="{ live: data.status === 'live' && !data.final }">
          <div class="side">
            <img v-if="data.away_logo" :src="data.away_logo" class="crest" alt="" />
            <div class="tn">{{ data.away_team }}</div>
            <div class="sc mono">{{ data.away_score ?? '—' }}</div>
          </div>
          <div class="mid">
            <div class="st" :class="{ livep: data.status === 'live' && !data.final, fin: data.final }">{{ statusLabel }}</div>
            <div class="vs">VS</div>
          </div>
          <div class="side">
            <img v-if="data.home_logo" :src="data.home_logo" class="crest" alt="" />
            <div class="tn">{{ data.home_team }}</div>
            <div class="sc mono">{{ data.home_score ?? '—' }}</div>
          </div>
        </div>

        <!-- this game's line + bet slip, directly under the hero/score —
             only while the game is still open for betting (before kickoff), and
             never for a self-excluded player (no play surfaces, GP-8 posture) -->
        <OddsBoard v-if="bettable && !user?.self_excluded" :sport="data.league" mode="event" :event-id="data.event_id" />

        <GameDetail :detail="data.detail || null" :home="data.home_team" :away="data.away_team" />
      </div>
      <div v-else-if="!pending" class="hint">Game not found.</div>
      <div v-else class="loading">loading…</div>
    </div>
  </div>
</template>

<style scoped>
.lg { color: var(--muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; margin: 4px 0 12px; }
.hero { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 12px;
  border: 1px solid var(--line); border-radius: 14px; background: var(--panel); padding: 22px 18px; margin-bottom: 18px; }
.hero.live { border-color: var(--warn); }
.side { text-align: center; }
.side .crest { width: 46px; height: 46px; object-fit: contain; margin-bottom: 8px; }
.tn { font-weight: 800; font-size: 17px; margin-bottom: 8px; }
.sc { font-size: 40px; font-weight: 800; line-height: 1; }
.mid { text-align: center; }
.st { font-family: var(--font-mono); font-size: 12px; color: var(--muted); margin-bottom: 6px; white-space: nowrap; }
.st.livep { color: var(--warn); }
.st.fin { color: var(--accent); }
.vs { font-style: italic; font-weight: 800; font-size: 30px; line-height: 1;
  letter-spacing: 0.02em; color: var(--ink);
  background: linear-gradient(90deg, var(--brand), var(--brand-2));
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.hint { color: var(--muted); padding: 8px 0 24px; }
.loading { color: var(--muted); text-align: center; padding: 36px 16px; }
@media (max-width: 560px) {
  .tn { font-size: 14px; }
  .sc { font-size: 30px; }
  .vs { font-size: 22px; }
}
</style>
